import {
  ChangeDetectionStrategy,
  Component,
  ComponentRef,
  computed,
  inject,
  OnDestroy,
  OnInit,
  signal,
  Type,
  ViewContainerRef,
  input,
  OutputRefSubscription,
  viewChild,
} from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { assertNever } from 'src/app/shared/utils/assert.utils';
import { setInputs } from 'src/app/shared/utils/component-ref.utils';
import { AlertController } from '@ionic/angular/standalone';
import { GameId } from 'src/app/shared/models/game/game-id';
import { AnalyticsService } from 'src/app/core/services/analytics/analytics.service';
import { ThemeService } from 'src/app/core/services/theme/theme.service';
import { UxGameService } from 'src/app/features/game/services/ux-game/ux-game.service';
import { PaymentService } from 'src/app/features/payments/services/payment.service';
import { OperatorService } from 'src/app/core/services/operator/operator.service';
import {
  ControlMode,
  GameOrchestrationService,
  GameStateEvent,
} from 'src/app/features/game/services/game-orchestration.service';
import { GameInterfaceState } from 'src/app/shared/models/game/game-interface-state';
import { GameScoreService } from 'src/app/features/game/services/game-score.service';
import { UxGameController } from '../services/controllers/game-legacy/ux-game-controller';
import { GenericSceneController } from '../services/controllers/scene-controllers/scene-controller-factory';
import { GameUiControlLoaders } from './game-ui-controls';
import { LoadingGameControlsComponent } from '../controls/loading-game-controls/loading-game-controls.component';
import { TierFirstControlsComponent } from '../controls/tier-first-controls/tier-first-controls.component';
import { ReplayGameControlsComponent } from '../controls/replay-game-controls/replay-game-controls.component';
import { GameControlDisplay } from '../services/game-ui/game-control-display';
import { GameScreenComponent } from '../game-screen/game-screen.component';
import { LoadingSpinnerComponent } from '../../../ui/feedback/loading-spinner/loading-spinner.component';
import { TierPlayEventData } from 'src/app/core/services/tiers/models/payment-events';

@Component({
  selector: 'app-game-host',
  templateUrl: './game-host.component.html',
  styleUrls: ['./game-host.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [LoadingSpinnerComponent, GameScreenComponent],
})
export class GameHostComponent implements OnInit, OnDestroy {
  // Optional: the container renders only in 'full' control mode, and clearControls() also runs
  // on destroy in 'game-only' mode.
  readonly gameControls = viewChild('gameControls', { read: ViewContainerRef });

  readonly controlMode = input<ControlMode>('full');

  orchestration = inject(GameOrchestrationService);

  private analyticsService = inject(AnalyticsService);
  private uxGameService = inject(UxGameService);
  private operatorService = inject(OperatorService);
  private paymentService = inject(PaymentService);
  private alertController = inject(AlertController);

  themeService = inject(ThemeService);
  gameScoreService = inject(GameScoreService);
  GameInterfaceState = GameInterfaceState;

  showingControls = computed(() => {
    const component = this.currentUiComponent();
    return (component?.instance as GameControlDisplay)?.showingControls?.() ?? false;
  });

  isTierFirstActive = signal(false);

  private currentUiComponent = signal<ComponentRef<unknown> | undefined>(undefined);
  private destroy$ = new Subject<void>();
  private loadingSubscriptions: OutputRefSubscription[] = [];
  private pendingOpenWagerDrawer = false;
  // A Solo result drawer's Play again resets to the loading controls and asks for the Solo sheet
  // in the same tick, before those controls exist, so the request waits for them here.
  private pendingOpenSoloSheet = false;

  ngOnInit(): void {
    if (this.controlMode() === 'full') {
      this.orchestration.stateEvents$.pipe(takeUntil(this.destroy$)).subscribe(async (event) => {
        await this.handleStateEvent(event);
      });

      this.uxGameService.openWagerDrawer$.pipe(takeUntil(this.destroy$)).subscribe(async () => {
        try {
          const instance = this.currentUiComponent()?.instance;
          if (instance instanceof LoadingGameControlsComponent) {
            await instance.openWagerModal();
          } else {
            this.pendingOpenWagerDrawer = true;
          }
        } catch (error) {
          this.analyticsService.handleError(error, 'Open wager drawer');
        }
      });

      this.uxGameService.openSoloSheet$.pipe(takeUntil(this.destroy$)).subscribe(async () => {
        try {
          const instance = this.currentUiComponent()?.instance;
          if (
            instance instanceof LoadingGameControlsComponent ||
            instance instanceof TierFirstControlsComponent
          ) {
            await instance.openSoloSheet();
          } else {
            this.pendingOpenSoloSheet = true;
          }
        } catch (error) {
          this.analyticsService.handleError(error, 'Open Solo sheet');
        }
      });
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.clearControls();
  }

  private async handleStateEvent(event: GameStateEvent): Promise<void> {
    const { state, controller, gameId } = event;
    const isLegacy = controller instanceof UxGameController;

    switch (state) {
      case GameInterfaceState.GameLoading:
        if (!this.gameScoreService.isPlayingAgain()) {
          this.gameScoreService.clear();
          this.gameScoreService.setGameName(controller.gameName);
          if (this.controlMode() === 'full' && !this.operatorService.isGameOnlyOperator()) {
            this.displayLoadingControls(gameId);
          }
        }
        break;
      case GameInterfaceState.GameStarted:
        await this.displayGameControls(gameId, controller);
        break;
      case GameInterfaceState.ReplayReady:
        if (!isLegacy) {
          this.displayReplayControls(controller as GenericSceneController);
        }
        break;
      case GameInterfaceState.GameWaiting:
      case GameInterfaceState.GameStarting:
      case GameInterfaceState.ReplayStarting:
      case GameInterfaceState.ReplayGameInit:
        this.clearControls();
        break;
      default:
        assertNever(state, 'Invalid game interface state');
    }
  }

  private resolveLoadingComponent(
    gameId: GameId,
  ): Type<LoadingGameControlsComponent | TierFirstControlsComponent> {
    if (gameId === GameId.Marbles) {
      // Marbles always has tier-first as it has no loading screen
      this.isTierFirstActive.set(true);
      return TierFirstControlsComponent;
    }

    if (OperatorService.IsDemo) {
      // Demos always have game-first
      this.isTierFirstActive.set(false);
      return LoadingGameControlsComponent;
    }

    const uxMode = this.themeService.playTabConfig().uxMode;

    if (uxMode === 'tier-first') {
      this.isTierFirstActive.set(true);
      return TierFirstControlsComponent;
    }

    this.isTierFirstActive.set(false);
    return LoadingGameControlsComponent;
  }

  private displayLoadingControls(gameId: GameId): void {
    this.clearControls();

    const componentClass = this.resolveLoadingComponent(gameId);
    const componentRef = this.requireGameControls().createComponent(componentClass);
    this.currentUiComponent.set(componentRef);
    const instance = componentRef.instance;

    // Set before the component's first change detection, so ngOnInit sees them.
    setInputs(componentRef, { gameId });

    // The type lets this key through for either component, so the instance check is what keeps it
    // off the tier-first controls.
    if (this.pendingOpenWagerDrawer && instance instanceof LoadingGameControlsComponent) {
      setInputs(componentRef, { autoOpenWagerDrawer: true });
      this.pendingOpenWagerDrawer = false;
    }

    // Both control components declare this input, and their ngOnInit opens the sheet once they
    // are mounted. The sheet pays for itself, so nothing comes back through showPaymentEvent.
    if (this.pendingOpenSoloSheet) {
      setInputs(componentRef, { autoOpenSoloSheet: true });
      this.pendingOpenSoloSheet = false;
    }

    this.loadingSubscriptions.push(
      instance.launchPracticeEvent.subscribe(() => {
        this.uxGameService.launchPracticeGame(0, 0);
      }),
    );

    this.loadingSubscriptions.push(
      instance.launchLocalEvent.subscribe(async () => {
        await this.launchLocalGame();
      }),
    );

    this.loadingSubscriptions.push(
      instance.showPaymentEvent.subscribe(async (data: TierPlayEventData) => {
        try {
          const result = await this.paymentService.showTierPaymentDrawer(gameId, data);
          if (result.dropped || result.role !== 'changePrizeAmount') {
            return;
          }
          // The tier-first list is still on screen under the drawer, so only the wager drawer
          // needs reopening.
          if (instance instanceof LoadingGameControlsComponent) {
            await instance.openWagerModal();
          }
        } catch (error) {
          this.analyticsService.handleError(error, 'Show payment');
        }
      }),
    );
  }

  private async displayGameControls(
    gameId: GameId,
    gameController: UxGameController | GenericSceneController,
  ): Promise<void> {
    try {
      const componentClass = await GameUiControlLoaders[gameId]();

      this.clearControls();
      const componentRef = this.requireGameControls().createComponent(componentClass);
      this.currentUiComponent.set(componentRef);
      // The lazy loader erases each component's controller type, so this name is not
      // compile-checked; game-host-inputs.spec.ts pins it on every game's controls.
      componentRef.setInput('gameController', gameController);
    } catch (error) {
      this.analyticsService.handleError(error, 'Load game UI');
      throw error;
    }
  }

  private displayReplayControls(gameController: GenericSceneController): void {
    this.clearControls();
    const componentRef = this.requireGameControls().createComponent(ReplayGameControlsComponent);
    this.currentUiComponent.set(componentRef);
    setInputs(componentRef, {
      gameController,
      resultDismissedCallback: () => this.uxGameService.resetToLoading(),
    });
  }

  // The controls are only shown in 'full' mode, where the container is always rendered.
  private requireGameControls(): ViewContainerRef {
    const gameControls = this.gameControls();
    if (!gameControls) {
      throw new Error('Game controls container is not rendered');
    }
    return gameControls;
  }

  private clearControls(): void {
    this.loadingSubscriptions.forEach((sub) => sub.unsubscribe());
    this.loadingSubscriptions = [];

    this.currentUiComponent()?.destroy();
    this.currentUiComponent.set(undefined);
    this.gameControls()?.clear();
    this.isTierFirstActive.set(false);
  }

  private async launchLocalGame(): Promise<void> {
    const alert = await this.alertController.create({
      cssClass: 'alert',
      header: 'Enter a seed number',
      inputs: [
        {
          name: 'number',
          type: 'number',
          placeholder: 'Seed number',
        },
      ],
      buttons: [
        {
          cssClass: 'alert-button',
          text: 'OK',
          handler: (data) => {
            const num = parseInt(data.number);
            this.uxGameService.launchLocalGame(Number.isFinite(num) ? num : undefined);
            alert.dismiss();
          },
        },
      ],
      backdropDismiss: false,
    });

    await alert.present();
    await alert.onDidDismiss();
  }
}
